// App-wide helpers: toasts, "pick a name first", report dialog, Take me back.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { NEED_PROFILE, store, useDB, type Result } from '../data/store';
import { visiblePosts, statsFor, userById } from '../data/queries';
import { RAINBOW } from '../lib/text';
import { REPORT_REASONS } from '../lib/validation';
import { YEARS, yearColor } from '../lib/years';
import type { ID, Report } from '../data/types';
import { Icon, Stamp } from './Bits';

type UI = {
  toast: (msg: string) => void;
  /** Runs an action; if it needs a profile first, asks for a name and then runs it. */
  act: <T>(fn: () => Result<T>, done?: (v: T) => void) => void;
  report: (type: Report['targetType'], id: ID) => void;
  takeMeBack: () => void;
  askProfile: () => void;
};
const Ctx = createContext<UI | null>(null);
export const useUI = (): UI => { const c = useContext(Ctx); if (!c) throw new Error('UIProvider missing'); return c; };

export function UIProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const [profileFor, setProfileFor] = useState<null | { then?: () => void }>(null);
  const [reportFor, setReportFor] = useState<null | { type: Report['targetType']; id: ID }>(null);
  const [tmb, setTmb] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const toast = useCallback((m: string) => { setMsg(m); window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setMsg(null), 2800); }, []);
  const act = useCallback(<T,>(fn: () => Result<T>, done?: (v: T) => void) => {
    const r = fn();
    if (r.ok) { done?.(r.value); return; }
    if (r.error === NEED_PROFILE) { setProfileFor({ then: () => { const r2 = fn(); if (r2.ok) done?.(r2.value); else toast(r2.error); } }); return; }
    toast(r.error);
  }, [toast]);
  const ui: UI = { toast, act, report: (type, id) => setReportFor({ type, id }), takeMeBack: () => setTmb(true), askProfile: () => setProfileFor({}) };

  return (
    <Ctx.Provider value={ui}>
      {children}
      {msg && <div className="toast" role="status">{msg}</div>}
      {profileFor && <ProfileDialog onClose={() => setProfileFor(null)} onDone={() => { const then = profileFor.then; setProfileFor(null); then?.(); }} />}
      {reportFor && <ReportDialog target={reportFor} onClose={() => setReportFor(null)} onDone={() => { setReportFor(null); toast('Thanks. A person will look at it.'); }} />}
      {tmb && <TakeMeBack onClose={() => setTmb(false)} />}
    </Ctx.Provider>
  );
}

export function Dialog({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    box.current?.querySelector<HTMLElement>('input,select,textarea,button')?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') close.current(); };
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('keydown', key); prev?.focus?.(); };
  }, []);
  return (
    <div className="scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={'dialog' + (wide ? ' wide' : '')} role="dialog" aria-modal="true" aria-label={title} ref={box}>
        <div className="dialog-head"><h2>{title}</h2><button className="ib" onClick={onClose} aria-label="Close"><Icon name="x" /></button></div>
        {children}
      </div>
    </div>
  );
}

function ProfileDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const db = useDB();
  const mine = userById(db, db.meId);
  const [f, setF] = useState({ handle: mine?.handle ?? '', name: mine?.name ?? '', color: mine?.color ?? 'pink', bio: mine?.bio ?? '', away: mine?.away ?? '' });
  const [age, setAge] = useState(mine ? '16' : '');
  const [err, setErr] = useState<Record<string, string>>({});
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!age) return setErr({ age: 'Please choose your age group.' });
    if (age === 'under13') return setErr({ age: 'Nostalgia is for people aged 13 and older.' });
    const r = store.saveProfile(f);
    if (!r.ok) return setErr(r.fields ?? { form: r.error });
    onDone();
  };
  return (
    <Dialog title={mine ? 'Edit your profile' : 'Pick a name first'} onClose={onClose}>
      <form className="form" onSubmit={submit} noValidate>
        <p className="muted small">Preview: your profile and posts are saved only in this browser until accounts launch.</p>
        <label className="fld"><span>Username</span><input value={f.handle} onChange={e => setF({ ...f, handle: e.target.value })} placeholder="e.g. cassette.kid" autoComplete="off" aria-invalid={!!err.handle} />{err.handle && <em>{err.handle}</em>}</label>
        <label className="fld"><span>Display name</span><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} maxLength={40} aria-invalid={!!err.name} />{err.name && <em>{err.name}</em>}</label>
        <fieldset className="fld"><legend>Your colour</legend>
          <div className="swatches">{RAINBOW.map(c => (
            <button type="button" key={c} className="swatch" aria-pressed={f.color === c} aria-label={c} style={{ background: `var(--rb-${c})` }} onClick={() => setF({ ...f, color: c })} />))}</div>
        </fieldset>
        <label className="fld"><span>Away message</span><input value={f.away} onChange={e => setF({ ...f, away: e.target.value })} maxLength={80} placeholder="brb, someone needs the phone line" /></label>
        <label className="fld"><span>Bio</span><textarea value={f.bio} onChange={e => setF({ ...f, bio: e.target.value })} maxLength={160} rows={2} /></label>
        {!mine && <label className="fld"><span>How old are you?</span>
          <select value={age} onChange={e => setAge(e.target.value)} aria-invalid={!!err.age}>
            <option value="">Choose</option><option value="16">16 or older</option><option value="13">13 to 15</option><option value="under13">Under 13</option>
          </select>
          {age === '13' && <small className="muted">When accounts launch, a parent will be asked to confirm by email before you can post.</small>}
          {err.age && <em>{err.age}</em>}
          <small className="muted">We don't store your age in the preview.</small></label>}
        {err.form && <p className="error">{err.form}</p>}
        <div className="row end"><button type="button" className="btn ghost" onClick={onClose}>Cancel</button><button className="btn hot">Save</button></div>
      </form>
    </Dialog>
  );
}

function ReportDialog({ target, onClose, onDone }: { target: { type: Report['targetType']; id: ID }; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState<string>('');
  const [details, setDetails] = useState('');
  const [sure, setSure] = useState(false);
  const [err, setErr] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!reason) return setErr('Please choose a reason.');
    if (!sure) return setErr('Please confirm the report is honest.');
    const r = store.report(target.type, target.id, { reason, details });
    if (!r.ok) return setErr(r.error);
    onDone();
  };
  return (
    <Dialog title="Report this" onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <label className="fld"><span>What's wrong?</span>
          <select value={reason} onChange={e => setReason(e.target.value)}><option value="">Choose a reason</option>{REPORT_REASONS.map(r => <option key={r}>{r}</option>)}</select></label>
        <label className="fld"><span>Details (optional)</span><textarea rows={3} value={details} onChange={e => setDetails(e.target.value)} maxLength={1000} placeholder="Where exactly is the problem? If it is about you, say how." /></label>
        <label className="chk"><input type="checkbox" checked={sure} onChange={e => setSure(e.target.checked)} />I believe this report is accurate and complete.</label>
        <p className="muted small">A person reviews every report. If we remove something, the poster gets a reason. In the preview, reports stay in this browser.</p>
        {err && <p className="error">{err}</p>}
        <div className="row end"><button type="button" className="btn ghost" onClick={onClose}>Cancel</button><button className="btn hot">Send report</button></div>
      </form>
    </Dialog>
  );
}

function TakeMeBack({ onClose }: { onClose: () => void }) {
  const db = useDB(), nav = useNavigate();
  const pool = visiblePosts(db);
  const pick = () => pool[Math.floor(Math.random() * pool.length)];
  const [post, setPost] = useState(pick);
  const [shown, setShown] = useState<number | null>(null);
  useEffect(() => {
    if (!post) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { setShown(post.year); return; }
    let n = 0;
    const t = window.setInterval(() => { n++; if (n > 14) { window.clearInterval(t); setShown(post.year); } else setShown(YEARS[Math.floor(Math.random() * YEARS.length)]!); }, 45);
    return () => window.clearInterval(t);
  }, [post]);
  if (!post) return <Dialog title="Take me back" onClose={onClose}><p>Nothing to travel to yet. Share the first memory.</p></Dialog>;
  const author = userById(db, post.authorId);
  return (
    <Dialog title="Take me back" onClose={onClose}>
      <div className="tm" style={{ ['--yc' as string]: yearColor(post.year) }}>
        <span className="line">Nostalgia pulled you back to</span>
        <Stamp className="tm-year" text={String(shown ?? post.year)} label={String(post.year)} />
        <p className="tm-title">{post.title}</p>
        <p>{post.body}</p>
        <p className="muted small">@{author?.handle} · {statsFor(db, post).reactions} people remember this</p>
        <div className="row between">
          <button className="btn ghost" onClick={onClose}>Back to now</button>
          <div className="row">
            <button className="btn ghost" onClick={() => { onClose(); nav(`/p/${post.id}`); }}>Open it</button>
            <button className="btn hot" onClick={() => setPost(pick())}><Icon name="rewind" />Again</button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
